/**
 * placementFlow — the UNIFIED state machine for what the current player must
 * resolve, in order, before they can freely place armies. It governs BOTH
 * ordinary placement AND the post-elimination interrupt with ONE code path, so
 * the two can never diverge (there is deliberately no separate "elimination"
 * flow).
 *
 * The order (each step SKIPPED when not applicable):
 *   1. banner        — "X was eliminated" overlay. Post-elimination only. Shown
 *                      first; auto-dismisses or is closed via ×.
 *   2. free-fortify  — move extra armies into the just-conquered territory.
 *                      Post-conquest only, and only while the source still has
 *                      an army to move (see freeFortifyActionable). May be
 *                      skipped.
 *   3. forced-turn-in— trade in a card set. Whenever the player holds ≥5 cards
 *                      (start-of-turn OR inherited from an eliminated player).
 *                      They must drop below 5 before placement resumes.
 *   4. placement     — place armies, then continue.
 *
 * In an ORDINARY turn there is no banner and no free-fortify, so the machine
 * reduces to steps 3→4: the forced turn-in still auto-opens whenever the player
 * enters placement holding ≥5 cards, otherwise they go straight to placement.
 * The post-elimination case is the same machine with steps 1–2 also in play.
 */

/** The conquest pair the engine leaves pending for a post-conquest free fortify. */
export interface PendingConquest {
  fromTerritoryId: string
  toTerritoryId: string
  armiesAlreadyMoved: number
}

/**
 * Whether a post-conquest free fortify is still ACTIONABLE: a conquest pair is
 * pending AND the source territory has at least one army it can legally move
 * (an occupied territory must keep 1 behind, so it needs ≥2 to transfer).
 *
 * Crucially this is NOT "is lastConquest set" — after an eliminating conquest
 * the engine keeps lastConquest set even when the source has only 1 army left,
 * so gating the turn-in modal on the raw flag soft-locked the player (nothing
 * to fortify, yet the modal deferred forever). Gate on real actionability.
 */
export function freeFortifyActionable(
  lastConquest: PendingConquest | null | undefined,
  territories: ReadonlyArray<{ id: string; armies: number }>,
): boolean {
  if (!lastConquest) return false
  const source = territories.find((t) => t.id === lastConquest.fromTerritoryId)
  if (!source) return false
  return source.armies - 1 >= 1
}

export type PlacementFlowStep = 'banner' | 'free-fortify' | 'forced-turn-in' | 'placement'

export interface PlacementFlowInput {
  /** A player-out banner (elimination/resignation) is still queued. */
  bannerPending: boolean
  /** A free fortify is still actionable + unresolved (see freeFortifyActionable). */
  freeFortifyPending: boolean
  /** The viewer owes a forced card turn-in (see viewerMustTurnIn). */
  mustTurnIn: boolean
}

/**
 * The step the current player is on. Pure priority cascade: banner first, then
 * free-fortify, then the forced turn-in, else placement. Each returns only when
 * its own precondition holds, so inapplicable steps fall through.
 */
export function placementFlowStep(input: PlacementFlowInput): PlacementFlowStep {
  if (input.bannerPending) return 'banner'
  if (input.freeFortifyPending) return 'free-fortify'
  if (input.mustTurnIn) return 'forced-turn-in'
  return 'placement'
}

/**
 * Whether the forced turn-in modal should auto-open right now: exactly when the
 * flow has reached the 'forced-turn-in' step (banner dismissed, free-fortify
 * resolved/not-applicable, and a turn-in is owed). Applies identically to a
 * normal placement turn and a post-elimination interrupt.
 */
export function shouldAutoOpenTurnInModal(input: PlacementFlowInput): boolean {
  return placementFlowStep(input) === 'forced-turn-in'
}
